"use client";

import React from "react";
import Link from "next/link";
import { SearchProduct } from "@/packages/types/products";
import Price from "@/components/common/Price";
import NextImage from "@/components/common/NextImage";

interface SearchDropdownV2Props {
  isOpen: boolean;
  loading: boolean;
  suggestions: SearchProduct[];
  totalCount: number;
  onSelectProduct: () => void;
  onViewAll: () => void;
}

export default function SearchDropdownV2({
  isOpen,
  loading,
  suggestions,
  totalCount,
  onSelectProduct,
  onViewAll,
}: SearchDropdownV2Props) {
  if (!isOpen) return null;

  return (
    <div className="absolute top-[54px] left-0 w-full bg-white rounded-[12px] border border-slate-200/70 shadow-[0_20px_50px_rgba(0,0,0,0.08)] overflow-hidden flex flex-col z-50">
      {loading ? (
        <div className="p-12 text-center text-sm text-slate-400 font-medium">
          Loading matching previews...
        </div>
      ) : suggestions.length > 0 ? (
        <>
          {/* One-Column Display Panel */}
          <div className="flex flex-col min-h-[340px]">
            {/* Search Results Column (100% Width) */}
            <div className="w-full p-7 flex-1 bg-white">
              <h3 className="text-[11px] font-bold text-slate-400 tracking-wider uppercase mb-5">
                Search Results
              </h3>

              {/* Grid/List of 3 Product Cards - Row layout on mobile, Grid on desktop */}
              <div className="flex flex-col sm:grid sm:grid-cols-3 gap-6 divide-y divide-slate-100 sm:divide-y-0">
                {suggestions.slice(0, 3).map((item, idx) => {
                  return (
                    <Link
                      href={`/products/${item.slug}`}
                      key={item.id}
                      className={`group flex flex-row sm:flex-col items-center sm:items-start gap-4 sm:gap-0 cursor-pointer ${
                        idx > 0 ? "pt-4 sm:pt-0" : ""
                      }`}
                      onClick={onSelectProduct}
                    >
                      {/* Compact Image Box */}
                      <div className="w-[60px] h-[60px] sm:w-full sm:h-[200px] bg-white border border-slate-100 rounded-md flex items-center justify-center overflow-hidden p-1 sm:p-4 group-hover:border-[var(--brand-accent)] transition-all duration-200 shrink-0">
                        <NextImage
                          src={item?.images?.[0]?.thumbnailUrl?.trim() || "/assets/site/images/default.png"}
                          alt={item.title}
                          width={160}
                          height={200}
                          className="object-contain max-h-full max-w-full group-hover:scale-[1.02] transition-transform duration-300"
                        />
                      </div>

                      {/* Info Column */}
                      <div className="flex-1 min-w-0 sm:mt-4">
                        <p className="text-[13px] font-semibold text-slate-700 line-clamp-2 leading-snug sm:min-h-[38px] group-hover:text-[var(--brand-accent)] transition-colors duration-150">
                          {item.title}
                        </p>

                        {/* Pricing Block */}
                        <div className="flex flex-col mt-1 sm:mt-1.5">
                          <span className="text-sm font-bold text-slate-800">
                            <Price amount={item.salePrice} />
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Bottom Footer Action */}
          <button
            type="button"
            onClick={onViewAll}
            className="w-full bg-white hover:bg-slate-50 text-center py-4 border-t border-slate-100 text-[13px] font-semibold text-slate-700 hover:text-[var(--brand-accent)] transition-all duration-200 cursor-pointer"
          >
            View all ({totalCount}) products
          </button>
        </>
      ) : (
        <div className="p-8 text-center text-xs text-slate-400 font-medium">
          No matching products found
        </div>
      )}
    </div>
  );
}
